import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import apiClient from '../services/api';

// Trae el usuario real desde el backend (/auth/me). Sin esto, el estado
// `user` de Redux nunca se llenaba con datos reales después de un login o
// registro exitoso — quedaba en null tras cualquier recarga de página, y
// por eso el avatar de la barra lateral siempre mostraba la "U" genérica
// en vez de la inicial real del cliente.
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/auth/me');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'No se pudo obtener el usuario actual' });
    }
  }
);

// 8 oct 2026 (Augusto: "necesito un demo... para que se coloque en su
// website"). Entra a PCM con la cuenta de ejemplo, sin contraseña y en
// modo solo lectura -- ver POST /auth/demo en el backend. Es la misma
// aplicación que usa un cliente, no una maqueta: por eso no se puede
// desactualizar.
export const entrarComoDemo = createAsyncThunk(
  'auth/demo',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post('/auth/demo');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Demo no disponible' });
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      localStorage.setItem('login_email', credentials.loginId);

      const loginData = {
        email: credentials.loginId,
        password: credentials.password
      };

      // FIX: esto armaba la URL a mano con axios + API_URL
      // (`${API_URL}/api/v1/auth/login`), y como API_URL en producción ya
      // incluye `/api/v1`, el resultado real era
      // `.../api/v1/api/v1/auth/login` -> 404. Es el mismo bug que rompió
      // el registro (ver la nota en services/api.js) pero acá nunca se
      // había corregido, así que el login estaba roto en producción.
      //
      // timeout de 60s (en vez de los 30s por defecto del cliente): el
      // backend está en el plan gratuito de Render, que "duerme" tras 15
      // min de inactividad y puede tardar 50s+ en despertar. Con 30s el
      // login fallaba justo cuando el servidor estaba por responder.
      const response = await apiClient.post('/auth/login', loginData, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      // Sin response = no llegó respuesta del servidor (timeout, red
      // caída, o el servidor todavía estaba despertando). Esto se marca
      // aparte para que la pantalla de login pueda reintentar solo en
      // este caso y mostrar un mensaje distinto a "contraseña incorrecta".
      const isNetworkOrTimeout = !error.response || error.code === 'ECONNABORTED';
      return rejectWithValue({
        ...(error.response?.data || { message: 'Login failed' }),
        isNetworkOrTimeout,
      });
    }
  }
);

// Login/registro con Google Identity Services (6 ago 2026, ver
// components/LoginForm.js y RegisterFormSinglePage.js). idToken es el
// credential que Google devuelve en el navegador; el backend lo verifica
// contra Google antes de emitir nuestro propio JWT (POST /auth/google) --
// mismo shape de respuesta (TokenResponse) que login/register normales.
export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (idToken, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/auth/google', { id_token: idToken }, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Google login error:', error);
      console.error('Error response:', error.response?.data);
      const isNetworkOrTimeout = !error.response || error.code === 'ECONNABORTED';
      return rejectWithValue({
        ...(error.response?.data || { message: 'Google login failed' }),
        isNetworkOrTimeout,
      });
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response?.data);
      return rejectWithValue(error.response?.data || { message: 'Registration failed' });
    }
  }
);

export const checkPhone = createAsyncThunk(
  'auth/checkPhone',
  async (phone, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/auth/check-phone?phone=${phone}`);
      return response.data;
    } catch (error) {
      console.error('Check phone error:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 403) {
        return rejectWithValue({ exists: false, error: 'Access denied' });
      }
      return rejectWithValue(error.response?.data || { exists: false });
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      // El formulario de UserProfile.js maneja los campos en camelCase
      // (occupation, employmentType, maritalStatus, hasDependents como
      // 'yes'/'no', dependentsCount, displayName...), pero el schema del
      // backend (UserProfileUpdate) usa snake_case y hasDependents como
      // boolean real — acá se traduce una sola vez.
      const payload = {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        phone_number: profileData.phone,
        display_name: profileData.displayName,
        country: profileData.country,
        city: profileData.city,
        occupation: profileData.occupation,
        employment_type: profileData.employmentType,
        marital_status: profileData.maritalStatus,
        has_dependents: profileData.hasDependents === 'yes',
        dependents_count: profileData.hasDependents === 'yes' ? Number(profileData.dependentsCount) || 0 : 0,
      };

      // Antes esto armaba la URL a mano con axios + API_URL, repitiendo el
      // mismo bug de `/api/v1/api/v1` que rompió el registro (ver
      // services/api.js) — usamos el cliente compartido, que ya tiene la
      // URL correcta y manda el token automáticamente.
      const response = await apiClient.put('/users/profile', payload);
      console.log('Profile update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Profile update error:', error);
      console.error('Error response:', error.response?.data);
      return rejectWithValue(error.response?.data || { message: 'Profile update failed: ' + error.message });
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Clear all relevant data from localStorage
      sessionStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('onboardingState');
      localStorage.removeItem('login_email');
      localStorage.removeItem('isNewUser');
      
      // Force reload to clear any in-memory state completely
      window.location.href = '/login';
      
      return { success: true };
    } catch (error) {
      return rejectWithValue({ message: 'Logout failed' });
    }
  }
);

export const deleteUser = createAsyncThunk(
  'auth/deleteUser',
  async (email, { rejectWithValue }) => {
    try {
      console.log(`Deleting user with email: ${email}`);

      // apiClient ya manda el token via interceptor; antes esto armaba la
      // URL a mano con axios + API_URL (mismo bug de /api/v1 duplicado).
      const response = await apiClient.delete('/users/delete', { data: { email } });

      console.log('User deletion response:', response.data);

      // Clear local storage on successful deletion
      sessionStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('onboardingState');
      localStorage.removeItem('login_email');
      localStorage.removeItem('isNewUser');
      localStorage.removeItem('profileCompleted');
      
      return response.data;
    } catch (error) {
      console.error('User deletion error:', error);
      console.error('Error response:', error.response?.data);
      // Devolver objeto de error completo para mejor manejo en el componente
      return rejectWithValue({ 
        message: error.response?.data?.message || error.message || 'Error al eliminar el usuario',
        response: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data || {}
        },
        code: error.code
      });
    }
  }
);

// Cargar el token desde sessionStorage al iniciar (3 ago 2026: se cambió de
// localStorage a sessionStorage a pedido de Augusto -- así, cerrar el
// navegador cierra la sesión, en vez de quedar disponible hasta 8 días
// después solo porque el token técnicamente no había vencido todavía). El
// usuario y el estado de onboarding sí se mantienen en localStorage, ya que
// no son credenciales y no tiene sentido perderlos entre pestañas.
const storedToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null;
const storedUser = typeof localStorage !== 'undefined' && localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;

// Parse onboarding state from localStorage if it exists
const storedOnboardingState = typeof localStorage !== 'undefined' && localStorage.getItem('onboardingState') 
  ? JSON.parse(localStorage.getItem('onboardingState')) 
  : null;

// Check if profile is completed
const profileCompleted = typeof localStorage !== 'undefined' ? localStorage.getItem('profileCompleted') === 'true' : false;

const initialState = {
  token: storedToken || null,
  user: storedUser || null,
  // 8 oct 2026: true cuando la sesión vino del botón "Ver demo" del sitio
  // web. Sobrevive un refresh dentro de la misma pestaña.
  esDemo: typeof sessionStorage !== 'undefined' && sessionStorage.getItem('pcm_demo') === '1',
  loading: false,
  error: null,
  profileCompleted: profileCompleted,
  isProfileUpdateLoading: false,
  profileUpdateError: null,
  phoneExists: false,
  phoneChecked: false,
  isNewUser: localStorage.getItem('isNewUser') === 'true',
  onboarding: {
    completed: storedOnboardingState?.completed || false,
    currentStep: storedOnboardingState?.currentStep || 'accounts',
    progress: storedOnboardingState?.progress || {
      accounts: false,
      debts: false,
      incomes: false,
      expenses: false,
      summary: false
    },
    data: storedOnboardingState?.data || {
      bankAccounts: [],
      debts: [],
      incomes: [],
      expenses: []
    },
    showDebtForm: storedOnboardingState?.showDebtForm || false
  }
};

// Acciones para actualizar el estado de onboarding
export const updateOnboardingStep = createAction('auth/updateOnboardingStep', (step) => ({
  payload: { step }
}));

// Acción para mostrar el formulario de deudas después de guardar cuentas
export const setShowDebtForm = createAction('auth/setShowDebtForm', (show) => ({
  payload: { show }
}));

export const updateOnboardingProgress = createAction('auth/updateOnboardingProgress', (progress) => ({
  payload: { progress }
}));

export const updateOnboardingData = createAction('auth/updateOnboardingData', (data) => ({
  payload: { data }
}));

export const completeOnboarding = createAction('auth/completeOnboarding');

// Acción para limpiar los datos del onboarding en el almacenamiento local
export const clearOnboardingLocalData = createAction('auth/clearOnboardingLocalData');

// Normaliza la respuesta del backend (snake_case: first_name, last_name,
// display_name, employment_type, marital_status, has_dependents,
// dependents_count, phone_number) al shape camelCase que ya usa el resto
// del frontend (firstName, lastName, displayName, etc.) — un solo lugar
// para esta traducción, usado por fetchCurrentUser y updateUserProfile.
const normalizeUser = (payload, previous) => ({
  ...previous,
  ...payload,
  firstName: payload.first_name,
  lastName: payload.last_name,
  displayName: payload.display_name || `${payload.first_name || ''} ${payload.last_name || ''}`.trim(),
  phone: payload.phone_number,
  employmentType: payload.employment_type,
  maritalStatus: payload.marital_status,
  hasDependents: payload.has_dependents,
  dependentsCount: payload.dependents_count,
});

// NOTA IMPORTANTE (bug real, ya corregido): este slice antes se armaba en
// tres pasos superpuestos (authSlice -> authSliceWithOnboarding ->
// authSliceWithOnboardingUpdated), y el último le pasaba a `createSlice`
// un `extraReducers: authSliceWithOnboarding.extraReducers` — pero el
// objeto que devuelve `createSlice()` NO tiene una propiedad
// `extraReducers` (solo existe como opción de *entrada*), así que ese
// valor era `undefined` y NINGUNO de los casos de login/register/logout/
// fetchCurrentUser/onboarding se aplicaba jamás al estado real. Por eso,
// por ejemplo, el avatar seguía mostrando "U" genérica incluso después de
// que `/auth/me` respondiera bien: la acción se disparaba, pero el
// reducer que la debía procesar nunca estaba conectado. Se consolida todo
// en un solo slice, con un solo builder, para que esto no vuelva a pasar.
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    markUserAsExisting: (state) => {
      state.isNewUser = false;
    },
    setManualLoginState: (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.onboarding = action.payload.onboarding;
      state.loading = false;
      state.error = null;
    },
    // 31 jul 2026: limpia un token que quedó en Redux/localStorage pero ya
    // no es válido en el backend (expiró a los 8 días, o se invalidó porque
    // el usuario cambió su contraseña — ver token_version en deps.py). A
    // diferencia de logout.fulfilled, esto NO navega a ningún lado — lo usa
    // LoginForm.js, que ya está mostrando el formulario de login y solo
    // necesita que el resto de la app (PublicHeader, que antes mostraba
    // "SALIR" con un token viejo) se entere de que la sesión ya terminó.
    clearStaleSession: (state) => {
      state.token = null;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        const token = action.payload.access_token || action.payload.token;
        state.token = token;
        state.user = normalizeUser(action.payload, state.user);
        sessionStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(entrarComoDemo.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token;
        state.user = normalizeUser(action.payload.usuario || {}, state.user);
        // Marca la sesión como demo para que la aplicación pueda avisarlo
        // (ver AvisoDemo.js). Va en sessionStorage, no en localStorage:
        // cerrar la pestaña termina la demostración, que es lo que uno
        // espera de algo que abrió desde una página web.
        state.esDemo = true;
        sessionStorage.setItem('token', action.payload.access_token);
        sessionStorage.setItem('pcm_demo', '1');
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(entrarComoDemo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || 'La demostración no está disponible.';
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Login failed';
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        const token = action.payload.access_token || action.payload.token;
        state.token = token;
        state.user = normalizeUser(action.payload, state.user);
        sessionStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Google login failed';
      })
      .addCase(checkPhone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkPhone.fulfilled, (state, action) => {
        state.loading = false;
        state.phoneExists = action.payload.exists;
        state.phoneChecked = true;
      })
      .addCase(checkPhone.rejected, (state) => {
        state.loading = false;
        state.phoneExists = false;
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token || action.payload.token || 'test-token';
        state.user = normalizeUser(
          {
            ...action.payload,
            first_name: action.payload.first_name || action.payload.firstName,
          },
          state.user
        );
        state.isNewUser = true;
        localStorage.setItem('isNewUser', 'true');
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Registration failed';
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.welcomeMessage = null;
        state.phoneExists = null;
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.welcomeMessage = null;
        state.phoneExists = null;
        window.location.href = '/login?deleted=true';
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Error al eliminar el usuario';
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = normalizeUser(action.payload, state.user);
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = normalizeUser(action.payload, state.user);
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(updateOnboardingStep, (state, action) => {
        state.onboarding.currentStep = action.payload.step;
        localStorage.setItem('onboardingState', JSON.stringify(state.onboarding));
      })
      .addCase(setShowDebtForm, (state, action) => {
        state.onboarding.showDebtForm = action.payload.show;
      })
      .addCase(updateOnboardingProgress, (state, action) => {
        state.onboarding.progress = {
          ...state.onboarding.progress,
          ...action.payload.progress
        };
        localStorage.setItem('onboardingState', JSON.stringify(state.onboarding));
      })
      .addCase(updateOnboardingData, (state, action) => {
        state.onboarding.data = {
          ...state.onboarding.data,
          ...action.payload.data
        };
        localStorage.setItem('onboardingState', JSON.stringify(state.onboarding));
      })
      .addCase(completeOnboarding, (state) => {
        state.onboarding.completed = true;
        state.onboarding.progress = {
          accounts: true,
          debts: true,
          incomes: true,
          expenses: true,
          summary: true
        };
        localStorage.setItem('onboardingState', JSON.stringify(state.onboarding));
      })
      .addCase(clearOnboardingLocalData, (state) => {
        state.onboarding = {
          ...state.onboarding,
          data: {
            bankAccounts: [],
            debts: [],
            incomes: [],
            expenses: []
          }
        };
        localStorage.removeItem('onboardingState');
      });
  },
});

export const { setToken, clearError, markUserAsExisting, setManualLoginState, clearStaleSession } = authSlice.actions;
export default authSlice.reducer;
